/* TCK subscription portal — Loop Storefront API client + UI.
 *
 * Auth doors, tried in order:
 *   1. Session token in the URL (magic-link arrivals; no Shopify login).
 *   2. Tokens already stored in this tab (sessionStorage).
 *   3. Loop's Shopify app proxy (identity attested by Shopify's own
 *      session — only works for logged-in customers).
 * Anything else renders the login prompt.
 *
 * The Storefront API scopes every token to one customer; the UI only
 * ever shows what the API returns for the authenticated customer.
 */
(function () {
  'use strict';

  var root = document.getElementById('tck-portal');
  if (!root) return;

  var config = JSON.parse(document.getElementById('tck-portal-config').textContent);
  var pickerData = JSON.parse(document.getElementById('tck-portal-picker-data').textContent);
  var serverCards = []; // menu-snippet-rendered cards; the picker enhances these when present
  var catalogEl = document.getElementById('tck-portal-catalog');
  var catalog = catalogEl ? JSON.parse(catalogEl.textContent) : {}; // variantId -> short description

  // The theme wraps content in animated/transformed ancestors, which
  // hijack position:fixed — the picker must live directly on <body>.
  var pickerEl = root.querySelector('.tck-portal__picker');
  document.body.appendChild(pickerEl);
  serverCards = Array.prototype.slice.call(pickerEl.querySelectorAll('[data-portal-product]'));
  if (serverCards.length) {
    // The cards ARE the menu page's cards — this list only backs the
    // draft logic (variant ids, prices).
    pickerData = serverCards.map(function (w) {
      return { el: w, isClub: w.hasAttribute('data-club'), variants: JSON.parse(w.dataset.variants) };
    });
  }

  /* ---------- in-page dialogs (no browser alert/confirm) ---------- */
  var modalEl = document.createElement('div');
  modalEl.className = 'tck-portal__modal';
  modalEl.hidden = true;
  modalEl.innerHTML = '<div class="tck-portal__modal-box"><h3 class="tck-portal__modal-title"></h3><p class="tck-portal__modal-msg"></p><div class="tck-portal__modal-actions"></div></div>';
  document.body.appendChild(modalEl);

  function uiDialog(title, message, buttons) {
    return new Promise(function (resolve) {
      var titleEl = modalEl.querySelector('.tck-portal__modal-title');
      titleEl.textContent = title || '';
      titleEl.hidden = !title;
      modalEl.querySelector('.tck-portal__modal-msg').textContent = message;
      var actions = modalEl.querySelector('.tck-portal__modal-actions');
      actions.innerHTML = '';
      buttons.forEach(function (b) {
        var el = document.createElement('button');
        el.type = 'button';
        el.className = 'tck-portal__btn' + (b.primary ? ' tck-portal__btn--primary' : '');
        el.textContent = b.label;
        el.onclick = function () { modalEl.hidden = true; resolve(b.value); };
        actions.appendChild(el);
      });
      modalEl.hidden = false;
      var primary = actions.querySelector('.tck-portal__btn--primary');
      if (primary) primary.focus();
    });
  }

  function uiNotice(title, message) {
    return uiDialog(title, message, [{ label: 'OK', primary: true, value: true }]);
  }

  function uiConfirm(title, message, yesLabel) {
    return uiDialog(title, message, [
      { label: yesLabel || 'Confirm', primary: true, value: true },
      { label: 'Cancel', value: false }
    ]);
  }

  /* Translate Loop's terser refusals into something a customer can act on. */
  function friendly(message) {
    var m = String(message || 'Something went wrong.');
    if (/minimum amount is not satisfied/i.test(m)) {
      return 'That change would take your box below the minimum order amount, so nothing was changed. To swap recipes, add the new one first, then remove the old one.';
    }
    if (/cannot update the quantity/i.test(m)) {
      return 'The subscription service didn’t accept that quantity change. Nothing was changed — try again in a moment, or add it as a separate product instead.';
    }
    return m;
  }
  var gbp = new Intl.NumberFormat('en-GB', { style: 'currency', currency: config.currency || 'GBP' });
  var TOKEN_KEY = 'tckPortalTokens';

  var state = {
    tokens: null,        // { accessToken, refreshToken }
    subscriptions: [],
    past: [],            // cancelled/expired, newest first
    currentId: null,
    frequencies: null,   // cached per current subscription
    customer: null       // GET /customer — addresses (with ids) + payment methods
  };

  /* ---------- token storage (sessionStorage may be unavailable) ---------- */
  function loadTokens() {
    try { return JSON.parse(sessionStorage.getItem(TOKEN_KEY)); } catch (e) { return null; }
  }
  function saveTokens(t) {
    state.tokens = t;
    try { sessionStorage.setItem(TOKEN_KEY, JSON.stringify(t)); } catch (e) { /* per-tab only */ }
  }
  function clearTokens() {
    state.tokens = null;
    try { sessionStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }
  }

  /* ---------- auth ---------- */
  function sessionTokenFromUrl() {
    var params = new URLSearchParams(window.location.search);
    return params.get('sessionToken') || params.get('st') || params.get('token');
  }

  function exchangeSessionToken(sessionToken) {
    return fetch(config.apiBase + '/auth/refreshToken', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: sessionToken })
    }).then(function (r) { return r.json(); }).then(function (json) {
      if (!json.success || !json.data) throw new Error('Session link expired — please log in.');
      saveTokens({ accessToken: json.data.accessToken, refreshToken: json.data.refreshToken });
    });
  }

  function proxyAuth() {
    // Loop's live proxy serves this as GET (their docs say POST, but the
    // live route 404s on POST — verified 24 Sep 2026).
    return fetch(config.proxyAuthPath, { method: 'GET', credentials: 'same-origin' })
      .then(function (r) {
        if (r.status === 401) throw { code: 'LOGIN' };
        return r.text();
      })
      .then(function (text) {
        var json;
        try { json = JSON.parse(text); } catch (e) {
          // Logged-out visitors get a themed "Sorry, can't find that page"
          // HTML response from the proxy rather than a 401 — that means
          // "please log in", not "something broke".
          if (!config.loggedInCustomer) throw { code: 'LOGIN' };
          throw new Error('The subscription service gave an unexpected response — try refreshing, or use the classic portal below.');
        }
        if (!json.success || !json.data) throw { code: 'LOGIN' };
        saveTokens({ accessToken: json.data.accessToken, refreshToken: json.data.refreshToken || null });
      });
  }

  function authenticate() {
    // Admin-copied portal links carry a ready-to-use JWT access token
    // in ?merchantToken=… — no exchange step needed (verified 24 Sep 2026).
    var params = new URLSearchParams(window.location.search);
    var direct = params.get('merchantToken');
    if (direct) {
      saveTokens({ accessToken: direct, refreshToken: null });
      var cleaned = new URL(window.location.href);
      cleaned.searchParams.delete('merchantToken');
      window.history.replaceState({}, '', cleaned.toString());
      return Promise.resolve();
    }
    var urlToken = sessionTokenFromUrl();
    if (urlToken) {
      return exchangeSessionToken(urlToken).then(function () {
        // Drop the token from the address bar so it can't be copied on.
        var url = new URL(window.location.href);
        ['sessionToken', 'st', 'token'].forEach(function (k) { url.searchParams.delete(k); });
        window.history.replaceState({}, '', url.toString());
      }).catch(function () {
        // Expired link: fall through to the other doors.
        return storedOrProxy();
      });
    }
    return storedOrProxy();
  }

  function storedOrProxy() {
    var stored = loadTokens();
    if (stored && stored.accessToken) { state.tokens = stored; return Promise.resolve(); }
    return proxyAuth();
  }

  /* ---------- API helper ---------- */
  /* Count in-flight WRITES so leaving the page mid-save warns first —
     the failure mode is someone closing the tab thinking a change
     landed when the request was still travelling. */
  var writesInFlight = 0;

  window.addEventListener('beforeunload', function (e) {
    var unsavedPicker = !pickerEl.hidden && typeof draftDirty === 'function' && draftDirty();
    if (writesInFlight > 0 || unsavedPicker) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  function api(path, opts, isRetry) {
    opts = opts || {};
    opts.headers = Object.assign({}, opts.headers, {
      'Authorization': 'Bearer ' + state.tokens.accessToken,
      'Content-Type': 'application/json'
    });
    var isWrite = opts.method && opts.method !== 'GET';
    if (isWrite && !isRetry) {
      writesInFlight++;
      var release = function () { writesInFlight = Math.max(0, writesInFlight - 1); };
      var p = apiRaw(path, opts, isRetry);
      p.then(release, release);
      return p;
    }
    return apiRaw(path, opts, isRetry);
  }

  function apiRaw(path, opts, isRetry) {
    return fetch(config.apiBase + path, opts).then(function (r) {
      if (r.status === 401 && !isRetry) {
        clearTokens();
        return proxyAuth().then(function () { return api(path, opts, true); });
      }
      return r.json().then(function (json) {
        if (!json.success) throw new Error(json.message || 'The subscription service returned an error.');
        return json;
      });
    });
  }

  /* ---------- data ---------- */
  function loadSubscriptions() {
    return api('/subscription').then(function (json) {
      // Live subscriptions run the page; cancelled/expired ones fold away
      // into a collapsed "Past subscriptions" list for reactivation.
      var all = json.data || [];
      var subs = all.filter(function (s) { return s.status === 'ACTIVE' || s.status === 'PAUSED'; });
      // Food boxes lead (active first); memberships sit last — people
      // come here to edit their calzones, not their membership.
      var rank = function (s) { return (isMembership(s) ? 2 : 0) + (s.status === 'ACTIVE' ? 0 : 1); };
      subs.sort(function (a, b) { return rank(a) - rank(b); });
      state.past = all.filter(function (s) { return s.status === 'CANCELLED' || s.status === 'EXPIRED'; })
        .sort(function (a, b) { return Date.parse(b.cancelledAt || b.updatedAt || 0) - Date.parse(a.cancelledAt || a.updatedAt || 0) || b.id - a.id; });
      state.subscriptions = subs;
      if (!subs.length) { setState('empty'); renderPast(); return; }
      if (!state.currentId || !findSub(state.currentId)) state.currentId = subs[0].id;
      state.frequencies = null;
      render();
      if (!pickerEl.hidden) { buildDraft(subs.filter(function (s) { return s.id === state.currentId; })[0] || subs[0]); renderPicker(); }
    });
  }

  function findSub(id) {
    return state.subscriptions.filter(function (s) { return s.id === id; })[0] || null;
  }

  function loadCustomer() {
    if (state.customer) return Promise.resolve(state.customer);
    return api('/customer').then(function (json) {
      state.customer = json.data || {};
      return state.customer;
    });
  }

  /* The subscription carries its shipping address inline; edits go to the
     customer address record, matched on street + postcode. */
  function findAddressId(sub) {
    var sa = sub.shippingAddress || {};
    var norm = function (s) { return String(s || '').toLowerCase().replace(/\s+/g, ''); };
    var match = (state.customer && state.customer.addresses || []).filter(function (a) {
      return norm(a.address1) === norm(sa.address1) && norm(a.zip) === norm(sa.zip);
    })[0];
    return match ? match.id : null;
  }

  /* Group a subscription's lines: bundles become one visual card.
   * A line belongs to a bundle when it carries a _bundleId attribute
   * (legacy Loop bundles / BYOB); its display name is _bundleName. */
  function groupLines(lines) {
    var groups = [];
    var byBundle = {};
    (lines || []).forEach(function (line) {
      var attrs = {};
      (line.attributes || []).forEach(function (a) { attrs[a.key] = a.value; });
      var bundleId = attrs._bundleId || null;
      if (bundleId) {
        if (!byBundle[bundleId]) {
          // live data carries bundleName (no underscore); docs say _bundleName — accept both
          byBundle[bundleId] = { type: 'bundle', bundleId: bundleId, name: attrs._bundleName || attrs.bundleName || 'Bundle', lines: [] };
          groups.push(byBundle[bundleId]);
        }
        byBundle[bundleId].lines.push(line);
      } else {
        groups.push({ type: 'line', line: line });
      }
    });
    return groups;
  }

  /* ---------- rendering ---------- */
  function setState(name, detail) {
    root.dataset.state = name;
    Array.prototype.forEach.call(root.querySelectorAll('.tck-portal__state'), function (el) {
      el.hidden = !el.classList.contains('tck-portal__state--' + name);
    });
    if (name === 'error') {
      root.querySelector('.tck-portal__error-detail').textContent = detail || '';
    }
  }

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  /* PAUSED subscriptions come back with discountedPrice zeroed on every
     line (Loop's portal shows £0.00 too) — fall back to price × qty for
     those ONLY. An active sub with a genuine 100% discount must show
     £0.00 truthfully, or the total lies about what will be billed. */
  function lineTotal(line, paused) {
    if (line.discountedPrice > 0 || !paused) return line.discountedPrice || 0;
    return (parseFloat(line.price) || 0) * (line.quantity || 1);
  }

  function subTotal(sub) {
    var goods = sub.totalLineItemDiscountedPrice || 0;
    if (goods <= 0 && sub.status === 'PAUSED') goods = sub.totalLineItemPrice || 0;
    return goods + (sub.deliveryPrice || 0);
  }

  /* Annual memberships (Calzone Club Membership) are subscriptions too,
     but "skip this delivery" and "add recipes" make no sense on them —
     they get a quiet renewal card instead. */
  function isMembership(sub) {
    return !!(sub.deliveryPolicy && sub.deliveryPolicy.interval === 'YEAR');
  }

  /* Loop contracts carry chargeOffsetDays: null in our store even though
     billing runs 7 days before delivery — the offset lives in Loop's
     portal settings, so we mirror it as a theme setting fallback. */
  function offsetDays(sub) {
    if (isMembership(sub)) return 0; // renewal date IS the billing date
    return sub.chargeOffsetDays != null ? sub.chargeOffsetDays : (config.deliveryOffsetDays || 0);
  }

  function deliveryDate(sub) {
    var epoch = sub.nextBillingDateEpoch || (sub.nextBillingDate ? Date.parse(sub.nextBillingDate) / 1000 : null);
    if (!epoch) return null;
    var d = new Date(epoch * 1000);
    d.setDate(d.getDate() + offsetDays(sub));
    return d;
  }

  function intervalLabel(policy) {
    if (!policy) return '';
    var unit = { WEEK: 'week', MONTH: 'month', DAY: 'day', YEAR: 'year' }[policy.interval] || policy.interval.toLowerCase();
    return policy.intervalCount === 1 ? 'every ' + unit : 'every ' + policy.intervalCount + ' ' + unit + 's';
  }

  function render() {
    var sub = findSub(state.currentId);
    if (!sub) return setState('empty');
    setState('portal');
    var membership = isMembership(sub);
    root.querySelector('.tck-portal__lines-head h2').textContent = membership ? 'Your membership' : 'Your recipes';
    root.querySelector('[data-action="open-picker"]').hidden = membership;
    var pauseRow = root.querySelector('.tck-portal__cancel-row--pause');
    if (pauseRow) pauseRow.hidden = sub.status === 'PAUSED'; // hero already offers Resume
    renderSwitcher();
    renderHero(sub);
    renderLines(sub);
    renderExtras(sub);
    renderPast();
  }

  /* ---------- extras: address, discount codes, payment ---------- */
  function renderExtras(sub) {
    var el = root.querySelector('.tck-portal__extras');
    var sa = sub.shippingAddress || {};
    var addressHtml =
      '<div class="tck-portal__extra" data-extra="address">' +
      '<div class="tck-portal__extra-head"><h3>Delivery address</h3>' +
      '<button type="button" class="tck-portal__btn tck-portal__btn--small" data-action="addr-edit">Edit</button></div>' +
      '<p class="tck-portal__extra-body">' +
      esc(((sa.firstName || '') + ' ' + (sa.lastName || '')).trim()) + '<br>' +
      esc(sa.address1 || '') + (sa.address2 ? '<br>' + esc(sa.address2) : '') + '<br>' +
      esc(sa.city || '') + ' ' + esc(sa.zip || '') +
      (sa.phone ? '<br>' + esc(sa.phone) : '') +
      '</p></div>';

    var codes = (sub.discounts || []).filter(function (d) { return d.isActive && d.type === 'CODE_DISCOUNT'; });
    var discountHtml =
      '<div class="tck-portal__extra" data-extra="discount">' +
      '<div class="tck-portal__extra-head"><h3>Discount code</h3></div>' +
      (codes.length ? '<ul class="tck-portal__codes">' + codes.map(function (d) {
        return '<li>' + esc(d.title) +
          ' <button type="button" class="tck-portal__link-btn" data-action="disc-remove" data-discount="' + esc(d.shopifyId) + '">Remove</button></li>';
      }).join('') + '</ul>' : '') +
      '<div class="tck-portal__extra-row">' +
      '<input type="text" class="tck-portal__extra-input" data-field="discount-code" placeholder="Discount code" autocomplete="off">' +
      '<button type="button" class="tck-portal__btn tck-portal__btn--small tck-portal__btn--primary" data-action="disc-apply">Apply</button>' +
      '</div></div>';

    var paymentHtml =
      '<div class="tck-portal__extra" data-extra="payment">' +
      '<div class="tck-portal__extra-head"><h3>Payment details</h3></div>' +
      '<p class="tck-portal__extra-body">Card details are updated through a secure link — we never handle your card on this page.</p>' +
      '<button type="button" class="tck-portal__btn tck-portal__btn--small" data-action="pay-update">Email me a secure update link</button>' +
      '</div>';

    el.innerHTML = addressHtml + discountHtml + paymentHtml;
  }

  function renderAddressForm(sub) {
    var card = root.querySelector('[data-extra="address"]');
    var sa = sub.shippingAddress || {};
    function field(label, name, value) {
      return '<label class="tck-portal__extra-field">' + label +
        '<input type="text" data-field="' + name + '" value="' + esc(value || '') + '"></label>';
    }
    card.innerHTML =
      '<div class="tck-portal__extra-head"><h3>Delivery address</h3></div>' +
      '<div class="tck-portal__extra-form">' +
      field('First name', 'firstName', sa.firstName) +
      field('Last name', 'lastName', sa.lastName) +
      field('Address line 1', 'address1', sa.address1) +
      field('Address line 2', 'address2', sa.address2) +
      field('City', 'city', sa.city) +
      field('Postcode', 'zip', sa.zip) +
      field('Phone', 'phone', sa.phone) +
      '<div class="tck-portal__extra-row">' +
      '<button type="button" class="tck-portal__btn tck-portal__btn--small tck-portal__btn--primary" data-action="addr-save">Save address</button>' +
      '<button type="button" class="tck-portal__btn tck-portal__btn--small" data-action="addr-cancel">Cancel</button>' +
      '</div></div>';
  }

  function pastSummary(sub) {
    var names = (sub.lines || []).map(function (l) {
      return (l.productTitle || l.name) + (l.quantity > 1 ? ' × ' + l.quantity : '');
    }).join(', ');
    if (names.length > 90) names = names.slice(0, 87) + '…';
    return names || 'No products';
  }

  function renderPast() {
    var wrap = root.querySelector('.tck-portal__past');
    wrap.hidden = !state.past.length;
    if (wrap.hidden) return;
    root.querySelector('.tck-portal__past-count').textContent = '(' + state.past.length + ')';
    var shown = state.past.slice(0, 10);
    root.querySelector('.tck-portal__past-list').innerHTML = shown.map(function (s) {
      return '<div class="tck-portal__past-row">' +
        '<div class="tck-portal__past-info">' +
        '<span class="tck-portal__past-status">' + (s.status === 'EXPIRED' ? 'Completed' : 'Cancelled') + '</span>' +
        '<span class="tck-portal__past-summary">' + esc(pastSummary(s)) + '</span>' +
        '</div>' +
        (s.status === 'CANCELLED' ? '<button type="button" class="tck-portal__btn tck-portal__btn--small" data-reactivate="' + s.id + '">Reactivate</button>' : '') +
        '</div>';
    }).join('') +
    (state.past.length > 10 ? '<p class="tck-portal__past-overflow">…and ' + (state.past.length - 10) + ' older in <a href="' + esc(config.classicPortalUrl) + '">the classic portal</a>.</p>' : '');
  }

  function renderSwitcher() {
    var el = root.querySelector('.tck-portal__switcher');
    el.hidden = state.subscriptions.length < 2;
    if (el.hidden) return;
    el.innerHTML = state.subscriptions.map(function (s) {
      return '<button type="button" class="tck-portal__chip' + (s.id === state.currentId ? ' is-active' : '') + '" data-sub="' + s.id + '">' +
        '#' + esc(s.shopifyId) + ' · ' + esc(s.status.toLowerCase()) + '</button>';
    }).join('');
  }

  function renderHero(sub) {
    var el = root.querySelector('.tck-portal__hero');
    var membership = isMembership(sub);
    var paused = sub.status === 'PAUSED';
    var date = deliveryDate(sub);
    var dateText = paused ? 'On hold' : (date ? date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }) : '—');
    var total = gbp.format(subTotal(sub));
    var freqLabel = intervalLabel(sub.deliveryPolicy);
    var freq = membership ? ' per year' : ' per delivery' + (paused ? ' · ' + esc(freqLabel) : '');
    var freqTitle = freqLabel.charAt(0).toUpperCase() + freqLabel.slice(1);
    var actions = membership ? '' : paused
      ? '<div class="tck-portal__hero-actions">' +
        '<button type="button" class="tck-portal__btn tck-portal__btn--primary" data-action="resume">Resume subscription</button>' +
        '</div>'
      : '<div class="tck-portal__hero-actions">' +
        '<button type="button" class="tck-portal__btn" data-action="skip">Skip this delivery</button>' +
        '<button type="button" class="tck-portal__btn" data-action="reschedule">Change date</button>' +
        '<button type="button" class="tck-portal__btn" data-action="frequency">' + esc(freqTitle) + ' ▾</button>' +
        '</div>';
    el.innerHTML =
      '<p class="tck-portal__hero-label">' + (membership ? 'Membership renews' : paused ? 'Subscription paused' : 'Next delivery') + '</p>' +
      '<p class="tck-portal__hero-date">' + esc(dateText) + '</p>' +
      '<p class="tck-portal__hero-meta">' + esc(total) + freq + '</p>' +
      (sub.status !== 'ACTIVE' && !paused ? '<p class="tck-portal__hero-meta tck-portal__hero-meta--status">' + esc(sub.status.toLowerCase()) + '</p>' : '') +
      actions;
  }

  function renderLines(sub) {
    var el = root.querySelector('.tck-portal__lines');
    var membership = isMembership(sub);
    var paused = sub.status === 'PAUSED';
    el.innerHTML = groupLines(sub.lines).map(function (g) {
      if (g.type === 'bundle') {
        var contents = g.lines.map(function (l) { return esc(l.name) + ' × ' + l.quantity; }).join('<br>');
        var total = g.lines.reduce(function (sum, l) { return sum + lineTotal(l, paused); }, 0);
        return '<div class="tck-portal__card tck-portal__card--row tck-portal__card--bundle">' +
          '<div class="tck-portal__card-body">' +
          '<h3>' + esc(g.name) + '</h3>' +
          '<p class="tck-portal__card-contents">' + contents + '</p>' +
          '<p class="tck-portal__card-price">' + gbp.format(total) + ' per delivery</p>' +
          '<a class="tck-portal__btn tck-portal__btn--small" href="' + esc(config.classicPortalUrl) + '">Edit bundle</a>' +
          '</div></div>';
      }
      var line = g.line;
      var lattrs = {};
      (line.attributes || []).forEach(function (a) { lattrs[a.key] = a.value; });
      // Compact row: they already own this — no sales copy needed.
      var variantLabel = line.variantTitle && line.variantTitle !== 'Default Title' ? line.variantTitle : '';
      return '<div class="tck-portal__card tck-portal__card--row" data-line="' + line.id + '">' +
        (line.image || line.variantImage ? '<img loading="lazy" src="' + esc(line.image || line.variantImage) + '" alt="">' : '<span class="tck-portal__card-noimg"></span>') +
        '<div class="tck-portal__card-body">' +
        '<h3>' + esc(line.productTitle || line.name) + '</h3>' +
        (lattrs['This delivery'] ? '<p class="tck-portal__card-variant">This delivery: ' + esc(lattrs['This delivery']) + '</p>' : '') +
        (line.isOneTimeRemoved ? '<p class="tck-portal__card-flag">Skipped for the next delivery</p>' : '') +
        '<p class="tck-portal__card-price tck-portal__card-price--split"><span>' + gbp.format(lineTotal(line, paused)) + (membership ? ' per year' : '') + '</span>' +
        (variantLabel ? '<span class="tck-portal__card-variant">' + esc(variantLabel) + '</span>' : '') + '</p>' +
        (membership ? '' :
        '<div class="tck-portal__card-actions">' +
        '<span class="tck-portal__qty">' +
        '<button type="button" data-action="qty" data-delta="-1" aria-label="Decrease">−</button>' +
        '<span>' + line.quantity + '</span>' +
        '<button type="button" data-action="qty" data-delta="1" aria-label="Increase">+</button>' +
        '</span>' +
        '<button type="button" class="tck-portal__link-btn" data-action="remove">Remove</button>' +
        '</div>') + '</div></div>';
    }).join('');
  }

  function defaultVariant(p) {
    if (p.selectedVid) {
      var picked = (p.variants || []).filter(function (v) { return v.id === p.selectedVid; })[0];
      if (picked) return picked;
    }
    var avail = (p.variants || []).filter(function (v) { return v.available; });
    return avail[0] || (p.variants || [])[0];
  }

  /* Variant dropdown (the tiny-popup look only exists in Chrome's
     device emulation — real phones show their native pickers, which
     are fine). Changing it re-prices the card live. */
  function variantPillsHtml(p, i, dv, locked) {
    if (!p.variants || p.variants.length < 2) return '';
    if (locked) return '<p class="tck-portal__pick-clubnote">' + esc(dv.title) + '</p>';
    return '<select class="tck-portal__pick-variant" data-index="' + i + '" aria-label="Choose an option">' +
      p.variants.map(function (v) {
        return '<option value="' + v.id + '" data-price="' + v.pricePence + '"' +
          (v.id === dv.id ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' +
          esc(v.title) + (v.available ? '' : ' — out of stock') + '</option>';
      }).join('') + '</select>';
  }

  /* variantId -> plain (non-bundle) subscription line, so picker cards
     can show what's already in the box. */
  function linesByVariant(sub) {
    var map = {};
    groupLines(sub && sub.lines).forEach(function (g) {
      if (g.type === 'line') map[g.line.variantShopifyId] = g.line;
    });
    return map;
  }

  /* ---------- picker draft: changes stage locally; one Save applies ---------- */
  var draft = null; // vid -> { qty, orig, lineId }

  function buildDraft(sub) {
    draft = {};
    var have = linesByVariant(sub);
    Object.keys(have).forEach(function (vid) {
      draft[vid] = { qty: have[vid].quantity, orig: have[vid].quantity, lineId: have[vid].id };
    });
  }

  function draftEntry(vid) {
    if (!draft[vid]) draft[vid] = { qty: 0, orig: 0, lineId: null };
    return draft[vid];
  }

  function draftDirty() {
    return !!draft && Object.keys(draft).some(function (vid) { return draft[vid].qty !== draft[vid].orig; });
  }

  function draftControls(vid) {
    var e = draft[vid];
    var qty = e ? e.qty : 0;
    if (qty > 0) {
      return '<div class="tck-portal__pick-have">' +
        '<span class="tck-portal__qty">' +
        '<button type="button" data-action="draft-qty" data-variant="' + vid + '" data-delta="-1" aria-label="Decrease">−</button>' +
        '<span>' + qty + '</span>' +
        '<button type="button" data-action="draft-qty" data-variant="' + vid + '" data-delta="1" aria-label="Increase">+</button>' +
        '</span><span class="tck-portal__pick-have-note">in your box</span></div>';
    }
    return '<button type="button" class="tck-portal__btn tck-portal__btn--primary" data-action="draft-add" data-variant="' + vid + '">Add</button>';
  }

  function renderPicker() {
    if (serverCards.length) { renderPickerFoot(); return renderPickerServer(); }
    var grid = pickerEl.querySelector('.tck-portal__picker-grid');
    var cards = [];

    // The rotating Club Special leads the picker, wearing its context:
    // the current recipe and the price-changes note travel on the card,
    // so nobody joins it unknowingly.
    var club = config.clubSpecial;
    if (club) {
      cards.push('<div class="tck-portal__pick tck-portal__pick--club">' +
        (club.image ? '<img loading="lazy" src="' + esc(club.image) + '" alt="">' : '') +
        '<h3>Calzone Club Special</h3>' +
        '<p class="tck-portal__pick-desc">Currently: <strong>' + esc(club.display) + '</strong></p>' +
        '<p class="tck-portal__pick-desc">Our rotating recipe — it changes each cycle and the price changes with each recipe.</p>' +
        '<p class="tck-portal__card-price">' + gbp.format(club.pricePence / 100) + '</p>' +
        draftControls(String(club.variantId)) +
        '</div>');
    }

    cards = cards.concat(pickerData.map(function (p, i) {
      var dv = defaultVariant(p);
      if (!dv) return '';
      var draftedVid = null;
      p.variants.forEach(function (v) { if (draft[v.id] && draft[v.id].qty > 0) { draftedVid = String(v.id); dv = v; } });
      var variantPicker = variantPillsHtml(p, i, dv, !!draftedVid);
      return '<div class="tck-portal__pick" data-index="' + i + '">' +
        (p.image ? '<img loading="lazy" src="' + esc(p.image) + '" alt="">' : '') +
        '<h3>' + esc(p.title) + '</h3>' +
        (p.desc ? '<p class="tck-portal__pick-desc">' + esc(p.desc) + '</p>' : '') +
        variantPicker +
        '<p class="tck-portal__card-price">' + gbp.format(dv.pricePence / 100) + '</p>' +
        draftControls(draftedVid || String(dv.id)) +
        '</div>';
    }));

    grid.innerHTML = cards.join('') || '<p class="tck-portal__pick-empty">Nothing is available to add right now.</p>';
    renderPickerFoot();
  }

  function renderPickerFoot() {
    var foot = pickerEl.querySelector('.tck-portal__picker-foot');
    if (!foot) {
      foot = document.createElement('div');
      foot.className = 'tck-portal__picker-foot';
      pickerEl.querySelector('.tck-portal__picker-inner').appendChild(foot);
    }
    foot.innerHTML = draftDirty()
      ? '<button type="button" class="tck-portal__btn tck-portal__btn--primary" data-action="picker-commit">Save changes</button>'
      : '<button type="button" class="tck-portal__btn" data-action="close-picker">Close</button>';
  }

  /* Enhance the menu-snippet cards: append the portal's controls strip
     (Add / stepper / variant choice) under each card. */
  function renderPickerServer() {
    pickerEl.querySelector('.tck-portal__picker-grid').classList.remove('is-updating');
    pickerData.forEach(function (p, i) {
      var ctr = p.el.querySelector('.tck-portal__pick-controls');
      if (!ctr) {
        ctr = document.createElement('div');
        ctr.className = 'tck-portal__pick-controls';
        p.el.appendChild(ctr);
      }
      var dv = defaultVariant(p);
      if (!dv) { ctr.innerHTML = ''; return; }
      var draftedVid = null;
      p.variants.forEach(function (v) { if (draft[v.id] && draft[v.id].qty > 0) { draftedVid = String(v.id); dv = v; } });
      var variantPicker = variantPillsHtml(p, i, dv, !!draftedVid);
      var clubNote = p.isClub && config.clubSpecial
        ? '<p class="tck-portal__pick-clubnote">Currently: <strong>' + esc(config.clubSpecial.display) + '</strong> — the price changes with each recipe.</p>'
        : '';
      ctr.innerHTML = clubNote + variantPicker + draftControls(draftedVid || String(dv.id));
    });
  }

  /* Point the card's Add at the chosen variant and re-price everything
     the card shows: fallback price, the menu card's price span, and the
     per-variant subscription was/now pair from the embedded map. */
  function applyVariantChoice(row, p, vid, pricePence) {
    if (p) p.selectedVid = vid;
    if (!row) return;
    var addBtn = row.querySelector('[data-action="draft-add"]');
    if (addBtn) addBtn.dataset.variant = String(vid);
    var priceEl = row.querySelector('.tck-portal__card-price');
    if (priceEl && pricePence) priceEl.textContent = gbp.format(pricePence / 100);
    var gridPrice = row.querySelector('.js-grid-price');
    if (gridPrice && pricePence) gridPrice.textContent = gbp.format(pricePence / 100);
    var subMapEl = row.querySelector('.menu-sub-prices');
    if (subMapEl) {
      try {
        var subMap = JSON.parse(subMapEl.textContent);
        var entry = subMap[String(vid)];
        var wasEl = row.querySelector('.menu-sub-price__was');
        var nowEl = row.querySelector('.menu-sub-price__now');
        if (entry && wasEl && nowEl) { wasEl.textContent = entry.was; nowEl.textContent = entry.now; }
      } catch (err) { /* map unreadable — leave prices as rendered */ }
    }
  }

  function onPickerChange(e) {
    var sel = e.target.closest('.tck-portal__pick-variant');
    if (!sel) return;
    var opt = sel.options[sel.selectedIndex];
    applyVariantChoice(
      sel.closest('[data-portal-product], .tck-portal__pick'),
      pickerData[Number(sel.dataset.index)],
      Number(sel.value),
      Number(opt.dataset.price) || 0
    );
  }

  /* ---------- Zapiet: postcode coverage + delivery calendar ----------
     Same public widget API the cart uses (POST delivery/locations with
     shop + postcode + items, then /calendar) — so the portal can never
     disagree with checkout, and it updates the moment Zapiet settings
     change. Every check FAILS OPEN: a checker outage never blocks a
     legitimate action, it just skips the extra validation. */
  function zapietItems(sub) {
    return (sub.lines || []).map(function (l) { return { id: l.variantShopifyId, quantity: l.quantity }; });
  }

  function zapietLocation(zip, sub) {
    return fetch('https://api.zapiet.com/v1.0/delivery/locations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ shop: config.shopDomain, geoSearchQuery: zip, shoppingCart: zapietItems(sub) })
    }).then(function (r) { return r.json(); });
  }

  function zapietCalendar(sub) {
    var zip = sub.shippingAddress && sub.shippingAddress.zip;
    if (!zip || !config.shopDomain) return Promise.reject(new Error('no data'));
    return zapietLocation(zip, sub).then(function (loc) {
      if (!loc || loc.error || !loc.id) throw new Error('no coverage');
      return fetch('https://api.zapiet.com/v1.0/delivery/locations/' + loc.id + '/calendar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: config.shopDomain, shoppingCart: zapietItems(sub) })
      }).then(function (r) { return r.json(); });
    }).then(function (cal) {
      if (!cal || cal.error || !cal.minDate) throw new Error('no calendar');
      var dates = {}, dows = {};
      (cal.disabled || []).forEach(function (x) {
        if (Array.isArray(x) && x.length === 3) dates[x[0] + '-' + (x[1] + 1) + '-' + x[2]] = true; // 0-based month
        else if (typeof x === 'number') dows[(x - 1 + 7) % 7] = true; // Zapiet 1=Sunday -> JS getDay() 0
      });
      return { min: cal.minDate, max: cal.maxDate, dates: dates, dows: dows };
    });
  }

  /* Month-grid date chooser. Writes the picked ISO date into the flow's
     hidden .tck-portal__date-input; if Zapiet can't be reached it turns
     that input into a plain date field (the previous behaviour). */
  function mountDateChooser(scope, sub) {
    var calEl = scope.querySelector('.tck-portal__cal');
    var input = scope.querySelector('.tck-portal__date-input');
    if (!calEl || !input) return;
    var ourMin = new Date();
    ourMin.setDate(ourMin.getDate() + offsetDays(sub) + 1);
    var iso = function (d) { return d.toISOString().slice(0, 10); };

    function fallback() {
      calEl.remove();
      input.type = 'date';
      input.min = iso(ourMin);
    }

    calEl.textContent = 'Loading available dates…';
    zapietCalendar(sub).then(function (rules) {
      var effMin = new Date(rules.min + 'T12:00:00');
      if (effMin < ourMin) effMin = ourMin;
      var effMax = new Date(rules.max + 'T12:00:00');
      if (effMax < effMin) return fallback();
      var cursor = new Date(effMin.getFullYear(), effMin.getMonth(), 1);

      function enabled(d) {
        if (d < effMin || d > effMax) return false;
        if (rules.dows[d.getDay()]) return false;
        if (rules.dates[d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate()]) return false;
        return true;
      }

      function draw() {
        var y = cursor.getFullYear(), m = cursor.getMonth();
        var monthName = cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        var firstDow = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-first
        var daysInMonth = new Date(y, m + 1, 0).getDate();
        var html = '<div class="tck-portal__cal-head">' +
          '<button type="button" class="tck-portal__cal-nav" data-cal-nav="-1" aria-label="Previous month">‹</button>' +
          '<span>' + esc(monthName) + '</span>' +
          '<button type="button" class="tck-portal__cal-nav" data-cal-nav="1" aria-label="Next month">›</button>' +
          '</div><div class="tck-portal__cal-grid">' +
          ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map(function (d) { return '<span class="tck-portal__cal-dow">' + d + '</span>'; }).join('');
        for (var i = 0; i < firstDow; i++) html += '<span></span>';
        for (var day = 1; day <= daysInMonth; day++) {
          var d = new Date(y, m, day, 12);
          var ok = enabled(d);
          var isoDay = iso(d);
          html += ok
            ? '<button type="button" class="tck-portal__cal-day' + (input.value === isoDay ? ' is-picked' : '') + '" data-cal-pick="' + isoDay + '">' + day + '</button>'
            : '<span class="tck-portal__cal-day is-off">' + day + '</span>';
        }
        html += '</div>';
        calEl.innerHTML = html;
        calEl.querySelector('[data-cal-nav="-1"]').disabled = (y === effMin.getFullYear() && m === effMin.getMonth());
        calEl.querySelector('[data-cal-nav="1"]').disabled = (y === effMax.getFullYear() && m === effMax.getMonth());
      }

      calEl.addEventListener('click', function (ev) {
        var nav = ev.target.closest('[data-cal-nav]');
        if (nav && !nav.disabled) { cursor.setMonth(cursor.getMonth() + Number(nav.dataset.calNav)); draw(); return; }
        var pick = ev.target.closest('[data-cal-pick]');
        if (pick) { input.value = pick.dataset.calPick; draw(); }
      });
      draw();
    }).catch(fallback);
  }

  function dateChooserHtml() {
    return '<div class="tck-portal__cal"></div><input type="hidden" class="tck-portal__date-input">';
  }

  /* Postcode sanity (postcodes.io) + delivery coverage (Zapiet). */
  function checkDeliveryPostcode(zip, sub) {
    return fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(zip))
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (j && j.status === 404) return { ok: false, msg: '“' + zip + '” doesn’t look like a valid UK postcode — please check it.' };
        return zapietLocation(zip, sub).then(function (loc) {
          if (loc && loc.error) return { ok: false, msg: 'We don’t currently deliver to ' + zip + ', so the address wasn’t changed.' };
          return { ok: true };
        });
      })
      .catch(function () { return { ok: true }; }); // checker down ≠ customer blocked
  }

  /* ---------- cancellation flow (reasons/offers all come from Loop
     admin config — nothing here is hardcoded copy except chrome) ---------- */
  var cancelEl = document.createElement('div');
  cancelEl.className = 'tck-portal__modal tck-portal__cancel';
  cancelEl.hidden = true;
  cancelEl.innerHTML = '<div class="tck-portal__modal-box tck-portal__cancel-box"></div>';
  document.body.appendChild(cancelEl);
  var cancelCtx = null;

  function cancelBox() { return cancelEl.querySelector('.tck-portal__cancel-box'); }
  function closeCancel() { cancelEl.hidden = true; cancelCtx = null; }

  function openCancelFlow(sub) {
    cancelCtx = { subId: sub.id, firstName: (sub.shippingAddress || {}).firstName || 'there', reasons: [], reason: null };
    cancelBox().innerHTML = '<p class="tck-portal__modal-msg">One moment…</p>';
    cancelEl.hidden = false;
    api('/cancellationFlow').then(function (json) {
      cancelCtx.reasons = (json.data || []).slice().sort(function (a, b) { return (a.position || 0) - (b.position || 0); });
      renderCancelReasons();
    }).catch(function (e) {
      closeCancel();
      uiNotice('Something went wrong', friendly(e.message));
    });
  }

  function renderCancelReasons() {
    cancelBox().innerHTML =
      '<h3 class="tck-portal__modal-title">Cancel your subscription</h3>' +
      (cancelCtx.reasons.length ? '<p class="tck-portal__modal-msg">Mind telling us why? It helps us do better.</p>' +
      '<div class="tck-portal__cancel-reasons">' +
      cancelCtx.reasons.map(function (r) {
        return '<label class="tck-portal__cancel-reason"><input type="radio" name="tck-cancel-reason" value="' + r.id + '"> ' + esc(r.reason) + '</label>';
      }).join('') + '</div>' +
      '<textarea class="tck-portal__cancel-comment" placeholder="Anything else you’d like us to know? (optional)" hidden></textarea>'
      : '<p class="tck-portal__modal-msg">Your future orders will stop. You can reactivate any time.</p>') +
      '<div class="tck-portal__modal-actions">' +
      '<button type="button" class="tck-portal__btn tck-portal__btn--primary" data-action="cancel-keep">Keep my subscription</button>' +
      '<button type="button" class="tck-portal__btn" data-action="cancel-continue">Continue</button>' +
      '</div>';
  }

  function renderCancelAlternatives() {
    var r = cancelCtx.reason || {};
    var alts = [];
    if (r.showSkipNext) alts.push('<button type="button" class="tck-portal__btn tck-portal__cancel-alt" data-action="cancel-skip">Skip my next delivery instead</button>');
    if (r.showDelay) alts.push('<button type="button" class="tck-portal__btn tck-portal__cancel-alt" data-action="cancel-delay">Move my next delivery date</button>');
    if (r.showPause) alts.push('<button type="button" class="tck-portal__btn tck-portal__cancel-alt" data-action="cancel-pause">Pause my subscription for now</button>');
    if (!alts.length) { doCancel(); return; }
    cancelBox().innerHTML =
      '<h3 class="tck-portal__modal-title">Before you go…</h3>' +
      '<p class="tck-portal__modal-msg">Would one of these work better? No hard feelings either way.</p>' +
      '<div class="tck-portal__cancel-alts">' + alts.join('') + '</div>' +
      '<div class="tck-portal__modal-actions">' +
      '<button type="button" class="tck-portal__link-btn" data-action="cancel-confirm">No thanks — cancel my subscription</button>' +
      '</div>';
  }

  function doCancel() {
    var body = { comment: cancelCtx.comment || '' };
    if (cancelCtx.reason) body.cancellationFlowId = cancelCtx.reason.id;
    var subId = cancelCtx.subId;
    cancelBox().innerHTML = '<p class="tck-portal__modal-msg">Cancelling…</p>';
    api('/subscription/' + subId + '/cancel', { method: 'POST', body: JSON.stringify(body) })
      .then(function () { return refresh(); })
      .then(function () {
        return api('/subscription/' + subId + '/instantWinbackOffer').catch(function () { return null; });
      })
      .then(function (json) {
        var offer = json && json.data;
        if (offer && offer.discountIncentiveId) { renderWinback(subId, offer); return; }
        closeCancel();
        uiNotice('Subscription cancelled', 'Your future orders have stopped. You can reactivate any time from “Past subscriptions” at the bottom of this page.');
      })
      .catch(function (e) {
        closeCancel();
        uiNotice('Subscription not cancelled', friendly(e.message));
      });
  }

  function mergeTags(html) {
    return String(html || '')
      .replace(/\{\{\s*customer_first_name\s*\}\}/g, esc(cancelCtx ? cancelCtx.firstName : 'there'))
      .replace(/\{\{\s*store_name\s*\}\}/g, 'The Calzone Kitchen')
      .replace(/\{\{\s*timer\s*\}\}/g, cancelCtx && cancelCtx.expiryHours ? cancelCtx.expiryHours + ' hours' : 'a limited time');
  }

  function renderWinback(subId, offer) {
    cancelCtx = cancelCtx || {};
    cancelCtx.subId = subId;
    cancelCtx.expiryHours = offer.winbackExpiryHours;
    var img = offer.desktopBannerUrl || offer.mobileBannerUrl;
    cancelBox().innerHTML =
      (img ? '<img class="tck-portal__cancel-banner" src="' + esc(img) + '" alt="">' : '') +
      '<div class="tck-portal__cancel-offer">' + mergeTags(offer.body) + '</div>' +
      (offer.offerExpiryText ? '<div class="tck-portal__cancel-expiry">' + mergeTags(offer.offerExpiryText) + '</div>' : '') +
      '<div class="tck-portal__modal-actions">' +
      '<button type="button" class="tck-portal__btn tck-portal__btn--primary" data-action="winback-accept" data-offer="' + esc(offer.discountIncentiveId) + '">' + esc(offer.keepSubscriptionCTAText || 'Claim offer & stay') + '</button>' +
      '<button type="button" class="tck-portal__btn" data-action="cancel-keep">' + esc(offer.cancelSubscriptionCTAText || 'No thanks') + '</button>' +
      '</div>';
  }

  /* ---------- pause with duration (mirrors Loop: fixed duration,
     auto-resumes; the preview shows the rescheduled delivery date) ---------- */
  var pauseCtx = null;

  function pauseDeliveryDate(weeks) {
    var d = new Date();
    d.setDate(d.getDate() + weeks * 7);
    return d;
  }

  function renderPauseDialog(sub, flowId) {
    pauseCtx = { subId: sub.id, flowId: flowId || null };
    var weeks = config.pauseWeeks && config.pauseWeeks.length ? config.pauseWeeks : [4, 6, 10];
    cancelBox().innerHTML =
      '<h3 class="tck-portal__modal-title">Pause subscription</h3>' +
      '<p class="tck-portal__modal-msg">Select pause duration — deliveries start again automatically afterwards.</p>' +
      '<div class="tck-portal__cancel-reasons">' +
      weeks.map(function (w, i) {
        return '<label class="tck-portal__cancel-reason"><input type="radio" name="tck-pause-weeks" value="' + w + '"' + (i === 0 ? ' checked' : '') + '> ' + w + ' weeks</label>';
      }).join('') + '</div>' +
      '<p class="tck-portal__pause-preview">Your next delivery will be rescheduled to:<br><strong>' +
      esc(pauseDeliveryDate(weeks[0]).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })) + '</strong></p>' +
      '<div class="tck-portal__modal-actions">' +
      '<button type="button" class="tck-portal__btn tck-portal__btn--primary" data-action="pause-confirm">Pause subscription</button>' +
      '<button type="button" class="tck-portal__btn" data-action="cancel-keep">Cancel</button>' +
      '</div>';
    cancelEl.hidden = false;
  }

  cancelEl.addEventListener('change', function (e) {
    var pauseRadio = e.target.closest('input[name="tck-pause-weeks"]');
    if (pauseRadio) {
      var pv = cancelBox().querySelector('.tck-portal__pause-preview strong');
      if (pv) pv.textContent = pauseDeliveryDate(Number(pauseRadio.value)).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      return;
    }
    if (!cancelCtx || !e.target.closest('input[name="tck-cancel-reason"]')) return;
    var picked = cancelCtx.reasons.filter(function (r) { return String(r.id) === e.target.value; })[0];
    cancelCtx.reason = picked || null;
    var comment = cancelBox().querySelector('.tck-portal__cancel-comment');
    if (comment) comment.hidden = !(picked && picked.showComment);
  });

  /* ---------- actions ---------- */
  /* Loop sometimes rejects a reschedule fired seconds after resume /
     reactivate (the contract is still transitioning) — retry briefly. */
  function rescheduleRetry(subId, epochSec, tries) {
    return api('/subscription/' + subId + '/reschedule', {
      method: 'POST',
      body: JSON.stringify({ newBillingDateEpoch: epochSec, timezone: 'Europe/London' })
    }).catch(function (e) {
      if (tries <= 1) throw e;
      return new Promise(function (res) { setTimeout(res, 2500); })
        .then(function () { return rescheduleRetry(subId, epochSec, tries - 1); });
    });
  }

  /* Some contracts (legacy, custom-priced lines) refuse the per-line
     quantity/remove endpoints; Loop's bulk lines endpoint is the other
     door their own portal can use — fall back to it before giving up. */
  function setLineQuantity(subId, lineId, quantity) {
    return api('/subscription/' + subId + '/line/' + lineId + '/quantity', {
      method: 'PUT', body: JSON.stringify({ quantity: quantity })
    }).catch(function (e) {
      return api('/subscription/' + subId + '/line', {
        method: 'PUT', body: JSON.stringify({ linesToUpdate: [{ lineId: lineId, quantity: quantity }] })
      }).catch(function () { throw e; }); // surface the original refusal
    });
  }

  function removeLine(subId, lineId) {
    return api('/subscription/' + subId + '/line/' + lineId, { method: 'DELETE' })
      .catch(function (e) {
        return api('/subscription/' + subId + '/line', {
          method: 'PUT', body: JSON.stringify({ linesToRemove: [{ lineId: lineId }] })
        }).catch(function () { throw e; });
      });
  }

  function busy(el, on) {
    if (!el) return;
    el.disabled = on;
    el.classList.toggle('is-busy', on);
  }

  function refresh() {
    return loadSubscriptions().catch(function (e) { setState('error', e.message); });
  }

  function onClick(e) {
    var btn = e.target.closest('[data-action], [data-sub], [data-reactivate], [data-reactivate-save]');
    if (!btn) return;
    var sub = findSub(state.currentId);

    if (btn.dataset.reactivate) {
      // Never restart billing on one tap: the customer first picks the
      // next delivery date, and the copy says plainly that this restarts
      // billing — an order is only ever scheduled, not placed on the spot.
      var pastSub = state.past.filter(function (s) { return s.id === Number(btn.dataset.reactivate); })[0];
      if (!pastSub) return;
      var rowEl = btn.closest('.tck-portal__past-row');
      rowEl.innerHTML =
        '<div class="tck-portal__past-info">' +
        '<span class="tck-portal__past-summary">' + esc(pastSummary(pastSub)) + '</span>' +
        '<span class="tck-portal__past-summary"><strong>This restarts your subscription and its billing.</strong> ' +
        'Your next order will be scheduled for the delivery date you choose — payment is taken 7 days before it, to your saved payment method.</span>' +
        '<span class="tck-portal__date-label">Next delivery date</span>' +
        dateChooserHtml() +
        '</div>' +
        '<span class="tck-portal__past-confirm">' +
        '<button type="button" class="tck-portal__btn tck-portal__btn--small tck-portal__btn--primary" data-reactivate-save="' + pastSub.id + '">Confirm &amp; reactivate</button> ' +
        '<button type="button" class="tck-portal__btn tck-portal__btn--small" data-action="reactivate-cancel">Cancel</button>' +
        '</span>';
      mountDateChooser(rowEl, pastSub);
      return;
    }

    if (btn.dataset.reactivateSave) {
      var raSub = state.past.filter(function (s) { return s.id === Number(btn.dataset.reactivateSave); })[0];
      var raInput = root.querySelector('.tck-portal__past .tck-portal__date-input');
      if (!raSub || !raInput) return;
      if (!raInput.value) { uiNotice('Not reactivated yet', 'Pick your next delivery date first.'); return; }
      var raDelivery = new Date(raInput.value + 'T09:00:00');
      if (isNaN(raDelivery)) return;
      var raBilling = new Date(raDelivery);
      raBilling.setDate(raBilling.getDate() - offsetDays(raSub));
      busy(btn, true);
      api('/subscription/' + raSub.id + '/reactivate', { method: 'POST', body: '{}' })
        .then(function () {
          return rescheduleRetry(raSub.id, Math.floor(raBilling.getTime() / 1000), 3).catch(function (e) {
            uiNotice('Reactivated — but date not set', 'Your subscription is reactivated, but the delivery date could not be set: ' + friendly(e.message) + ' Use "Change date" to pick it.');
          });
        })
        .then(function () { state.currentId = raSub.id; return refresh(); })
        .catch(function (err) { uiNotice('Subscription not reactivated', friendly(err.message) + ' If this keeps happening, use the classic portal link below.'); busy(btn, false); });
      return;
    }

    if (btn.dataset.sub) {
      state.currentId = Number(btn.dataset.sub);
      state.frequencies = null;
      render();
      return;
    }

    switch (btn.dataset.action) {
      case 'retry':
        setState('loading');
        refresh();
        break;
      case 'open-picker':
        buildDraft(sub);
        renderPicker();
        pickerEl.hidden = false;
        document.body.style.overflow = 'hidden';
        initPickerHeadScroll();
        break;
      case 'close-picker':
        pickerEl.hidden = true;
        document.body.style.overflow = '';
        break;
      case 'reactivate-cancel':
        renderPast();
        break;
      case 'addr-edit':
        renderAddressForm(sub);
        break;
      case 'addr-cancel':
        renderExtras(sub);
        break;
      case 'addr-save': {
        var form = root.querySelector('[data-extra="address"]');
        var val = function (name) { var i = form.querySelector('[data-field="' + name + '"]'); return i ? i.value.trim() : ''; };
        if (!val('lastName') || !val('address1') || !val('city') || !val('zip')) {
          uiNotice('Address not saved', 'Last name, address line 1, city and postcode are needed.');
          return;
        }
        busy(btn, true);
        form.classList.add('is-updating');
        checkDeliveryPostcode(val('zip'), sub).then(function (check) {
          if (!check.ok) {
            uiNotice('Address not saved', check.msg);
            busy(btn, false);
            form.classList.remove('is-updating');
            throw { code: 'HANDLED' };
          }
          return loadCustomer();
        }).then(function () {
          var addressId = findAddressId(sub);
          if (!addressId) throw new Error('This address can only be changed in the classic portal — use the link below.');
          var sa = sub.shippingAddress || {};
          // Loop's docs nest the fields under "address", but the live
          // endpoint validates them at the top level (its own error
          // names them bare) — send both shapes to be safe.
          var fields = {
            firstName: val('firstName'), lastName: val('lastName'),
            address1: val('address1'), address2: val('address2'),
            city: val('city'), zip: val('zip'), phone: val('phone'),
            countryCode: sa.countryCode || 'GB', provinceCode: sa.provinceCode || ''
          };
          var payload = Object.assign({}, fields, { address: fields });
          return api('/address/' + addressId, { method: 'PUT', body: JSON.stringify(payload) });
        }).then(function () {
          state.customer = null; // refetch next time — it changed
          return refresh();
        }).then(function () {
          uiNotice('Address updated', 'Your future deliveries will go to the new address.');
        }).catch(function (e) {
          if (e && e.code === 'HANDLED') return;
          uiNotice('Address not saved', friendly(e.message)); busy(btn, false);
          form.classList.remove('is-updating');
        });
        break;
      }
      case 'disc-apply': {
        var codeInput = root.querySelector('[data-field="discount-code"]');
        var code = codeInput ? codeInput.value.trim() : '';
        if (!code) return;
        busy(btn, true);
        api('/subscription/' + sub.id + '/discount', { method: 'POST', body: JSON.stringify({ code: code }) })
          .then(refresh).catch(function (e) { uiNotice('Code not applied', friendly(e.message)); }).then(function () { busy(btn, false); });
        break;
      }
      case 'disc-remove':
        uiConfirm('Remove discount code?', 'The discount will no longer apply to future orders.', 'Remove code').then(function (ok) {
          if (!ok) return;
          busy(btn, true);
          var discPanel = btn.closest('[data-extra="discount"]');
          if (discPanel) discPanel.classList.add('is-updating');
          api('/subscription/' + sub.id + '/discount/' + encodeURIComponent(btn.dataset.discount), { method: 'DELETE' })
            .then(refresh).catch(function (e) { uiNotice('Code not removed', friendly(e.message)); }).then(function () { busy(btn, false); });
        });
        break;
      case 'pay-update':
        busy(btn, true);
        loadCustomer().then(function (cust) {
          var methodId = sub.paymentMethodId || ((cust.paymentMethods || [])[0] || {}).id;
          if (!methodId) throw new Error('Payment details for this subscription can only be updated in the classic portal — use the link below.');
          return api('/paymentMethod/' + methodId + '/update', { method: 'POST', body: '{}' });
        }).then(function () {
          uiNotice('Check your email', 'We\'ve sent you a secure link to update your card.');
        }).catch(function (e) { uiNotice('Link not sent', friendly(e.message)); }).then(function () { busy(btn, false); });
        break;
      case 'pause-open':
        renderPauseDialog(sub);
        break;
      case 'pause-confirm': {
        var weeksInput = cancelBox().querySelector('input[name="tck-pause-weeks"]:checked');
        if (!weeksInput) return;
        var pauseWeeks = Number(weeksInput.value);
        var pauseBody = { pauseDuration: { intervalCount: pauseWeeks, intervalType: 'WEEK' } };
        if (pauseCtx && pauseCtx.flowId) pauseBody.cancellationFlowId = pauseCtx.flowId;
        var pauseSubId = (pauseCtx && pauseCtx.subId) || sub.id;
        busy(btn, true);
        api('/subscription/' + pauseSubId + '/pause', { method: 'POST', body: JSON.stringify(pauseBody) })
          .then(function () { closeCancel(); return refresh(); })
          .then(function () {
            uiNotice('Subscription paused', 'Deliveries resume automatically — your next delivery is planned for ' +
              pauseDeliveryDate(pauseWeeks).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) + '.');
          })
          .catch(function (e) { closeCancel(); uiNotice('Subscription not paused', friendly(e.message)); });
        break;
      }
      case 'cancel-open':
        openCancelFlow(sub);
        break;
      case 'cancel-keep':
        closeCancel();
        break;
      case 'cancel-continue': {
        if (cancelCtx && cancelCtx.reasons.length && !cancelCtx.reason) {
          return; // a reason must be picked first; the radios make this obvious
        }
        var commentEl = cancelBox().querySelector('.tck-portal__cancel-comment');
        if (cancelCtx) cancelCtx.comment = commentEl && !commentEl.hidden ? commentEl.value.trim() : '';
        renderCancelAlternatives();
        break;
      }
      case 'cancel-skip':
        api('/subscription/' + cancelCtx.subId + '/skipNext', { method: 'POST', body: JSON.stringify({ cancellationFlowId: cancelCtx.reason ? cancelCtx.reason.id : undefined }) })
          .then(function () { closeCancel(); return refresh(); })
          .then(function () { uiNotice('Delivery skipped', 'Your next delivery is skipped — the one after stays as scheduled.'); })
          .catch(function (e) { closeCancel(); uiNotice('Delivery not skipped', friendly(e.message)); });
        break;
      case 'cancel-delay': {
        closeCancel();
        var rsBtn = root.querySelector('[data-action="reschedule"]');
        if (rsBtn) rsBtn.click();
        break;
      }
      case 'cancel-pause':
        renderPauseDialog({ id: cancelCtx.subId }, cancelCtx.reason ? cancelCtx.reason.id : null);
        break;
      case 'cancel-confirm':
        doCancel();
        break;
      case 'winback-accept':
        busy(btn, true);
        api('/subscription/' + cancelCtx.subId + '/instantWinbackOffer', { method: 'POST', body: JSON.stringify({ instantWinbackOfferId: btn.dataset.offer }) })
          .then(function () { closeCancel(); return refresh(); })
          .then(function () { uiNotice('Welcome back!', 'Your subscription is active again with the offer applied.'); })
          .catch(function (e) { busy(btn, false); uiNotice('Offer not applied', friendly(e.message)); });
        break;
      case 'toggle-past': {
        var list = root.querySelector('.tck-portal__past-list');
        list.hidden = !list.hidden;
        btn.classList.toggle('is-open', !list.hidden);
        break;
      }
      case 'draft-add': {
        var addVid = btn.dataset.variant;
        draftEntry(addVid).qty = 1;
        renderPicker();
        break;
      }
      case 'draft-qty': {
        var dqEntry = draftEntry(btn.dataset.variant);
        dqEntry.qty = Math.max(0, dqEntry.qty + Number(btn.dataset.delta));
        renderPicker();
        break;
      }
      case 'picker-commit': {
        var adds = [], updates = [], removes = [];
        Object.keys(draft).forEach(function (vid) {
          var e2 = draft[vid];
          if (e2.qty === e2.orig) return;
          if (!e2.lineId && e2.qty > 0) adds.push({ variantShopifyId: Number(vid), quantity: e2.qty, isUpsell: true });
          else if (e2.lineId && e2.qty > 0) updates.push({ lineId: e2.lineId, quantity: e2.qty });
          else if (e2.lineId && e2.qty === 0) removes.push({ lineId: e2.lineId });
        });
        if (!adds.length && !updates.length && !removes.length) return;
        var bulk = {};
        if (adds.length) bulk.linesToAdd = adds;
        if (updates.length) bulk.linesToUpdate = updates;
        if (removes.length) bulk.linesToRemove = removes;
        busy(btn, true);
        pickerEl.querySelector('.tck-portal__picker-grid').classList.add('is-updating');
        api('/subscription/' + sub.id + '/line', { method: 'PUT', body: JSON.stringify(bulk) })
          .catch(function (e) { pickerEl.querySelector('.tck-portal__picker-grid').classList.remove('is-updating'); throw e; })
          .then(function () {
            pickerEl.hidden = true;
            document.body.style.overflow = '';
            return refresh();
          })
          .catch(function (e) { uiNotice('Changes not saved', friendly(e.message)); busy(btn, false); });
        break;
      }
      case 'skip':
        uiConfirm('Skip this delivery?', 'Your next delivery will be skipped — the one after stays as scheduled.', 'Skip delivery').then(function (ok) {
          if (!ok) return;
          busy(btn, true);
          api('/subscription/' + sub.id + '/skipNext', { method: 'POST', body: '{}' })
            .then(refresh).catch(function (e) { uiNotice('Delivery not skipped', friendly(e.message)); }).then(function () { busy(btn, false); });
        });
        break;
      case 'reschedule': {
        // Interim inline date field (a Zapiet-aware calendar replaces this):
        // the customer picks the DELIVERY date only; billing is silently
        // offsetDays earlier, which also sets the minimum choosable date.
        var actionsEl = btn.parentElement;
        actionsEl.innerHTML =
          '<div class="tck-portal__date-wrap"><span class="tck-portal__date-label">New delivery date</span>' +
          dateChooserHtml() +
          '<div class="tck-portal__hero-actions">' +
          '<button type="button" class="tck-portal__btn tck-portal__btn--primary" data-action="reschedule-save">Save</button>' +
          '<button type="button" class="tck-portal__btn" data-action="reschedule-cancel">Cancel</button>' +
          '</div>' +
          '<p class="tck-portal__date-note">Payment is taken 7 days before your delivery.</p></div>';
        mountDateChooser(actionsEl, sub);
        break;
      }
      case 'reschedule-cancel':
        renderHero(sub);
        break;
      case 'reschedule-save': {
        var input = root.querySelector('.tck-portal__date-input');
        if (!input || !input.value) { uiNotice('Date not changed', 'Pick a delivery date first.'); return; }
        var newDelivery = new Date(input.value + 'T09:00:00');
        if (isNaN(newDelivery)) return;
        // The API reschedules the BILLING date; billing runs offsetDays before delivery.
        var billing = new Date(newDelivery);
        billing.setDate(billing.getDate() - offsetDays(sub));
        busy(btn, true);
        api('/subscription/' + sub.id + '/reschedule', {
          method: 'POST',
          body: JSON.stringify({ newBillingDateEpoch: Math.floor(billing.getTime() / 1000), timezone: 'Europe/London' })
        }).then(refresh).catch(function (e) { uiNotice('Date not changed', friendly(e.message)); busy(btn, false); });
        break;
      }
      case 'resume': {
        // Let the customer pick their next delivery date BEFORE resuming,
        // so resuming never means an immediate order. Loop's resume call
        // takes no date, so we resume and then reschedule in one flow.
        var resumeActions = btn.parentElement;
        resumeActions.innerHTML =
          '<div class="tck-portal__date-wrap"><span class="tck-portal__date-label">Next delivery date</span>' +
          dateChooserHtml() +
          '<div class="tck-portal__hero-actions">' +
          '<button type="button" class="tck-portal__btn tck-portal__btn--primary" data-action="resume-save">Resume</button>' +
          '<button type="button" class="tck-portal__btn" data-action="resume-cancel">Cancel</button>' +
          '</div>' +
          '<p class="tck-portal__date-note">Payment is taken 7 days before your delivery.</p></div>';
        mountDateChooser(resumeActions, sub);
        break;
      }
      case 'resume-cancel':
        renderHero(sub);
        break;
      case 'resume-save': {
        var resumeInput = root.querySelector('.tck-portal__date-input');
        if (!resumeInput || !resumeInput.value) { uiNotice('Not resumed yet', 'Pick your next delivery date first.'); return; }
        var resumeDelivery = new Date(resumeInput.value + 'T09:00:00');
        if (isNaN(resumeDelivery)) return;
        var resumeBilling = new Date(resumeDelivery);
        resumeBilling.setDate(resumeBilling.getDate() - offsetDays(sub));
        busy(btn, true);
        api('/subscription/' + sub.id + '/resume', { method: 'POST', body: '{}' })
          .then(function () {
            return rescheduleRetry(sub.id, Math.floor(resumeBilling.getTime() / 1000), 3).catch(function (e) {
              uiNotice('Resumed — but date not set', 'Your subscription is resumed, but the delivery date could not be set: ' + friendly(e.message) + ' Use "Change date" to pick it.');
            });
          })
          .then(refresh)
          .catch(function (e) { uiNotice('Subscription not resumed', friendly(e.message)); busy(btn, false); });
        break;
      }
      case 'frequency': {
        busy(btn, true);
        api('/subscription/' + sub.id + '/frequency').then(function (json) {
          // Week-based intervals only: anything else (monthly) drifts off
          // our delivery weekdays and lands orders on weekends.
          var opts = (json.data || []).filter(function (o) { return o.deliveryInterval === 'WEEK'; });
          if (!opts.length) { uiNotice('Frequency', 'No other frequencies are available for this subscription.'); return; }
          var sel = document.createElement('select');
          sel.className = 'tck-portal__freq-select';
          var currentLabel = intervalLabel(sub.deliveryPolicy);
          sel.innerHTML = '<option value="">' + esc(currentLabel) + '</option>' + opts.map(function (o, i) {
            var label = intervalLabel({ interval: o.deliveryInterval, intervalCount: o.deliveryIntervalCount });
            return label === currentLabel ? '' : '<option value="' + i + '">' + esc(label) + '</option>';
          }).join('');
          btn.replaceWith(sel);
          sel.focus();
          sel.onchange = function () {
            if (sel.value === '') { renderHero(sub); return; }
            var o = opts[Number(sel.value)];
            sel.disabled = true;
            api('/subscription/' + sub.id + '/frequency', {
              method: 'PUT',
              // live API requires nextBillingDateEpoch (docs say optional):
              // send the current one so only the cadence changes.
              body: JSON.stringify({
                billingPolicy: { interval: o.billingInterval, intervalCount: o.billingIntervalCount },
                nextBillingDateEpoch: sub.nextBillingDateEpoch
              })
            }).then(refresh).catch(function (e) { uiNotice('Frequency not changed', friendly(e.message)); renderHero(sub); });
          };
          sel.onblur = function () { if (!sel.disabled && sel.value === '') renderHero(sub); };
        }).catch(function (e) { uiNotice('Frequency not changed', friendly(e.message)); }).then(function () { busy(btn, false); });
        break;
      }
      case 'qty': {
        var card = btn.closest('[data-line]');
        var lineId = Number(card.dataset.line);
        var line = (sub.lines || []).filter(function (l) { return l.id === lineId; })[0];
        if (!line) return;
        var next = line.quantity + Number(btn.dataset.delta);
        if (next < 1) return; // removal is explicit, never via the stepper
        busy(btn, true);
        card.classList.add('is-updating');
        setLineQuantity(sub.id, lineId, next)
          .then(refresh).catch(function (e) { uiNotice('Quantity not changed', friendly(e.message)); }).then(function () { busy(btn, false); });
        break;
      }
      case 'remove': {
        var rcard = btn.closest('[data-line]');
        uiConfirm('Remove this recipe?', 'It will be removed from every future delivery.', 'Remove').then(function (ok) {
          if (!ok) return;
          busy(btn, true);
          rcard.classList.add('is-updating');
          removeLine(sub.id, Number(rcard.dataset.line))
            .then(refresh).catch(function (e) { uiNotice('Recipe not removed', friendly(e.message)); }).then(function () { busy(btn, false); });
        });
        break;
      }
    }
  }

  /* Site-header behaviour for the picker's title bar: hide scrolling
     down, reappear on the first upward scroll. */
  var pickerHeadScrollBound = false;
  function initPickerHeadScroll() {
    if (pickerHeadScrollBound) return;
    var grid = pickerEl.querySelector('.tck-portal__picker-grid');
    var head = pickerEl.querySelector('.tck-portal__picker-head');
    if (!grid || !head) return;
    var lastTop = 0;
    grid.addEventListener('scroll', function () {
      var top = grid.scrollTop;
      if (top > lastTop && top > 40) head.classList.add('is-hidden');
      else if (top < lastTop) head.classList.remove('is-hidden');
      lastTop = top;
    }, { passive: true });
    pickerHeadScrollBound = true;
  }

  /* ---------- boot ---------- */
  root.addEventListener('click', onClick);
  pickerEl.addEventListener('click', onClick); // pickerEl lives on <body>, outside root
  pickerEl.addEventListener('change', onPickerChange);
  cancelEl.addEventListener('click', onClick);

  authenticate()
    .then(loadSubscriptions)
    .catch(function (e) {
      if (e && e.code === 'LOGIN') return setState('login');
      setState('error', e && e.message);
    });
})();
